import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, Circle, FabricImage, Rect, Textbox, Triangle } from 'fabric';
import { generateAiTemplate } from '../services/aiService';
import {
  createOrderApi,
  exportPdfApi,
  getDesignsApi,
  getOrdersApi,
  saveDesignApi,
  submitOrderToProviderApi,
  updateOrderStatusApi
} from '../services/designService';

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 400;
const STORAGE_KEY_JSON = 'postcard_saas_design_json';
const STORAGE_KEY_PNG = 'postcard_saas_design_png';
const ORDER_TIMELINE = ['pending', 'processing', 'printed', 'shipped', 'delivered'];
const FALLBACK_ORDER_STATUSES = [...ORDER_TIMELINE, 'failed'];
const FALLBACK_PROVIDERS = ['lob', 'click2mail', 'postalytics'];

const buttonBase =
  'rounded-xl px-4 py-2 text-sm font-semibold transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

const actionButton =
  `${buttonBase} bg-white/90 text-slate-800 shadow hover:-translate-y-0.5 hover:shadow-md`;

const primaryButton =
  `${buttonBase} bg-brandBlue text-white hover:-translate-y-0.5 hover:bg-blue-700 focus-visible:ring-brandBlue`;

const dangerButton =
  `${buttonBase} bg-brandRed text-white hover:-translate-y-0.5 hover:bg-red-700 focus-visible:ring-brandRed`;

const addShadow = (obj) => {
  obj.set({
    shadow: '0 8px 18px rgba(15, 23, 42, 0.16)'
  });
  return obj;
};

const formatDate = (value) => {
  try {
    return new Date(value).toLocaleString();
  } catch (_error) {
    return 'Unknown date';
  }
};

const base64ToBlob = (base64, mimeType = 'application/pdf') => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeType });
};

const statusChipClass = (status) => {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'pending') return 'bg-amber-100 text-amber-700';
  if (normalized === 'processing') return 'bg-sky-100 text-sky-700';
  if (normalized === 'printed' || normalized === 'shipped' || normalized === 'delivered') {
    return 'bg-emerald-100 text-emerald-700';
  }

  return 'bg-rose-100 text-rose-700';
};

const timelineStepClass = (currentStatus, step) => {
  const current = String(currentStatus || '').toLowerCase();

  if (current === 'failed') {
    return 'bg-slate-100 text-slate-500';
  }

  const currentIndex = ORDER_TIMELINE.indexOf(current);
  const stepIndex = ORDER_TIMELINE.indexOf(step);

  if (currentIndex >= 0 && stepIndex <= currentIndex) {
    return 'bg-emerald-100 text-emerald-700';
  }

  return 'bg-slate-100 text-slate-500';
};

function Editor({ token = '' }) {
  const canvasElementRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState('');
  const [qrValue, setQrValue] = useState('https://example.com/special-offer');
  const [statusMessage, setStatusMessage] = useState('Editor ready. Start designing your postcard.');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [isLoadingDesigns, setIsLoadingDesigns] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [updatingStatusOrderId, setUpdatingStatusOrderId] = useState(null);
  const [submittingProviderOrderId, setSubmittingProviderOrderId] = useState(null);

  const [designs, setDesigns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeDesignId, setActiveDesignId] = useState(null);
  const [allowedOrderStatuses, setAllowedOrderStatuses] = useState(FALLBACK_ORDER_STATUSES);
  const [supportedProviders, setSupportedProviders] = useState(FALLBACK_PROVIDERS);
  const [statusDraftByOrderId, setStatusDraftByOrderId] = useState({});
  const [providerDraftByOrderId, setProviderDraftByOrderId] = useState({});

  const [orderForm, setOrderForm] = useState({
    full_name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });

  const aiPrompts = useMemo(
    () => [
      'Birthday postcard for John',
      'Summer sale postcard for Miami customers',
      'Real estate open house postcard for Austin'
    ],
    []
  );
  const [prompt, setPrompt] = useState(aiPrompts[0]);

  useEffect(() => {
    if (!canvasElementRef.current) return undefined;

    const editorCanvas = new Canvas(canvasElementRef.current, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true
    });

    fabricCanvasRef.current = editorCanvas;

    const updatePreview = () => {
      const url = editorCanvas.toDataURL({ format: 'png', quality: 1 });
      setPreviewUrl(url);
    };

    editorCanvas.on('object:added', updatePreview);
    editorCanvas.on('object:modified', updatePreview);
    editorCanvas.on('object:removed', updatePreview);
    updatePreview();

    return () => {
      editorCanvas.off('object:added', updatePreview);
      editorCanvas.off('object:modified', updatePreview);
      editorCanvas.off('object:removed', updatePreview);
      editorCanvas.dispose();
    };
  }, []);

  const getCanvas = () => fabricCanvasRef.current;

  const loadMyDesigns = async (targetToken) => {
    if (!targetToken) {
      setDesigns([]);
      return;
    }

    try {
      setIsLoadingDesigns(true);
      const items = await getDesignsApi({ token: targetToken });
      setDesigns(items);
    } catch (error) {
      console.error('Failed to load designs:', error);
      setStatusMessage(error.message || 'Could not load your saved designs.');
    } finally {
      setIsLoadingDesigns(false);
    }
  };

  const loadMyOrders = async (targetToken) => {
    if (!targetToken) {
      setOrders([]);
      return;
    }

    try {
      setIsLoadingOrders(true);
      const payload = await getOrdersApi({ token: targetToken });
      const loadedOrders = payload.orders || [];

      setOrders(loadedOrders);

      if (payload.allowedStatuses?.length) {
        setAllowedOrderStatuses(payload.allowedStatuses);
      }
      if (payload.supportedProviders?.length) {
        setSupportedProviders(payload.supportedProviders);
      }

      setStatusDraftByOrderId((prev) => {
        const next = { ...prev };
        loadedOrders.forEach((order) => {
          if (!next[order.id]) {
            next[order.id] = order.status || 'pending';
          }
        });
        return next;
      });

      setProviderDraftByOrderId((prev) => {
        const next = { ...prev };
        const defaultProvider = payload.supportedProviders?.[0] || supportedProviders[0] || 'lob';
        loadedOrders.forEach((order) => {
          if (!next[order.id]) {
            next[order.id] = defaultProvider;
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to load orders:', error);
      setStatusMessage(error.message || 'Could not load your print orders.');
    } finally {
      setIsLoadingOrders(false);
    }
  };

  useEffect(() => {
    loadMyDesigns(token);
    loadMyOrders(token);
  }, [token]);

  const addText = () => {
    const canvas = getCanvas();
    if (!canvas) return;

    const text = addShadow(
      new Textbox('Your headline here', {
        left: 120,
        top: 140,
        width: 360,
        fontSize: 42,
        fontFamily: 'Georgia',
        fill: '#0F172A',
        fontWeight: 700,
        textAlign: 'center'
      })
    );

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setStatusMessage('Text box added. Drag, resize, or rotate it.');
  };

  const addShape = (type) => {
    const canvas = getCanvas();
    if (!canvas) return;

    let shape;

    if (type === 'rect') {
      shape = new Rect({
        left: 40,
        top: 50,
        width: 160,
        height: 95,
        fill: '#F59E0B',
        rx: 16,
        ry: 16,
        stroke: '#B45309',
        strokeWidth: 2
      });
    } else if (type === 'circle') {
      shape = new Circle({
        left: 420,
        top: 55,
        radius: 48,
        fill: '#2563EB',
        stroke: '#1E3A8A',
        strokeWidth: 2
      });
    } else {
      shape = new Triangle({
        left: 260,
        top: 260,
        width: 96,
        height: 90,
        fill: '#DC2626',
        stroke: '#7F1D1D',
        strokeWidth: 2
      });
    }

    addShadow(shape);
    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.renderAll();
    setStatusMessage(`Added a ${type} shape.`);
  };

  const onUploadImage = async (event) => {
    const canvas = getCanvas();
    const file = event.target.files?.[0];
    if (!canvas || !file) return;

    try {
      const localUrl = URL.createObjectURL(file);
      const img = await FabricImage.fromURL(localUrl);

      img.set({
        left: 190,
        top: 95,
        scaleX: 0.35,
        scaleY: 0.35,
        cornerStyle: 'circle'
      });

      addShadow(img);
      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      URL.revokeObjectURL(localUrl);

      setStatusMessage('Image added to canvas.');
    } catch (error) {
      console.error('Image upload failed', error);
      setStatusMessage('Could not load image. Please try another file.');
    } finally {
      event.target.value = '';
    }
  };

  const addQrCode = async () => {
    const canvas = getCanvas();
    if (!canvas) return;

    const encoded = encodeURIComponent(qrValue || 'https://example.com');
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encoded}`;

    try {
      const qr = await FabricImage.fromURL(qrUrl, {
        crossOrigin: 'anonymous'
      });

      qr.set({
        left: 444,
        top: 225,
        scaleX: 0.5,
        scaleY: 0.5,
        cornerStyle: 'circle'
      });

      addShadow(qr);
      canvas.add(qr);
      canvas.setActiveObject(qr);
      canvas.renderAll();
      setStatusMessage('QR code added.');
    } catch (error) {
      console.error('QR insert failed', error);
      setStatusMessage('QR code failed to load. Check internet and try again.');
    }
  };

  const removeSelected = () => {
    const canvas = getCanvas();
    if (!canvas) return;

    const active = canvas.getActiveObjects();
    if (!active.length) {
      setStatusMessage('No selected layer to delete.');
      return;
    }

    active.forEach((obj) => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.renderAll();
    setStatusMessage('Selected layer removed.');
  };

  const clearCanvas = () => {
    const canvas = getCanvas();
    if (!canvas) return;

    canvas.clear();
    canvas.backgroundColor = '#ffffff';
    canvas.renderAll();
    setStatusMessage('Canvas cleared.');
  };

  const applyAiTemplatePlaceholder = () => {
    const canvas = getCanvas();
    if (!canvas) return;

    canvas.clear();

    const bg = new Rect({
      left: 0,
      top: 0,
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      fill: '#E0F2FE'
    });

    const banner = new Rect({
      left: 24,
      top: 24,
      width: 552,
      height: 92,
      fill: '#2563EB',
      rx: 18,
      ry: 18
    });

    const title = new Textbox('AI Template Suggestion', {
      left: 44,
      top: 42,
      width: 510,
      fontSize: 34,
      fontFamily: 'Georgia',
      fontWeight: 700,
      fill: '#FFFFFF',
      textAlign: 'center'
    });

    const promptText = new Textbox(prompt, {
      left: 52,
      top: 154,
      width: 496,
      fontSize: 24,
      fontFamily: 'Arial',
      fill: '#0F172A',
      textAlign: 'center'
    });

    const cta = new Rect({
      left: 180,
      top: 302,
      width: 240,
      height: 56,
      fill: '#DC2626',
      rx: 28,
      ry: 28
    });

    const ctaText = new Textbox('Edit Me', {
      left: 230,
      top: 316,
      width: 140,
      fontSize: 24,
      fontFamily: 'Arial',
      fontWeight: 700,
      fill: '#ffffff',
      textAlign: 'center'
    });

    [bg, banner, title, promptText, cta, ctaText].forEach((obj) => {
      addShadow(obj);
      canvas.add(obj);
    });

    canvas.renderAll();
    setStatusMessage('AI placeholder template applied. All elements remain editable.');
  };

  const applyAiTemplateFromApi = async () => {
    const canvas = getCanvas();
    if (!canvas) return;

    if (!token) {
      setStatusMessage('No JWT token found. Saved prompt applied using local AI placeholder.');
      applyAiTemplatePlaceholder();
      return;
    }

    try {
      setIsGeneratingAi(true);
      setStatusMessage('Generating template from AI endpoint...');

      const template = await generateAiTemplate({ prompt, token });
      const json = typeof template === 'string' ? JSON.parse(template) : template;

      await canvas.loadFromJSON(json);
      canvas.renderAll();

      setStatusMessage('AI template loaded from API. You can now edit all layers.');
    } catch (error) {
      console.error('AI API template generation failed:', error);
      setStatusMessage('AI API request failed. Applied local placeholder template instead.');
      applyAiTemplatePlaceholder();
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const saveDesign = async () => {
    const canvas = getCanvas();
    if (!canvas) return;

    const designJson = JSON.stringify(canvas.toJSON());
    const png = canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });

    localStorage.setItem(STORAGE_KEY_JSON, designJson);
    localStorage.setItem(STORAGE_KEY_PNG, png);

    if (!token) {
      setStatusMessage('Design saved locally. Login to sync it to your account.');
      return;
    }

    try {
      setIsSavingDesign(true);
      const saved = await saveDesignApi({
        token,
        designJson,
        previewImage: png,
        size: '6x4'
      });

      if (saved?.designId) {
        setActiveDesignId(saved.designId);
      }

      setStatusMessage('Design saved locally and synced to your account.');
      await loadMyDesigns(token);
    } catch (error) {
      console.error('Failed to sync design:', error);
      setStatusMessage(error.message || 'Saved locally, but cloud sync failed.');
    } finally {
      setIsSavingDesign(false);
    }
  };

  const loadDesignFromList = async (designItem) => {
    const canvas = getCanvas();
    if (!canvas) return;

    try {
      const json =
        typeof designItem.design_json === 'string'
          ? JSON.parse(designItem.design_json)
          : designItem.design_json;

      await canvas.loadFromJSON(json);
      canvas.renderAll();
      setActiveDesignId(designItem.id);
      setStatusMessage(`Loaded design #${designItem.id}.`);
    } catch (error) {
      console.error('Failed to load saved design:', error);
      setStatusMessage('Could not load that saved design.');
    }
  };

  const exportPng = () => {
    const canvas = getCanvas();
    if (!canvas) return;

    const png = canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });
    const link = document.createElement('a');
    link.href = png;
    link.download = 'postcard-design.png';
    link.click();

    setStatusMessage('PNG exported.');
  };

  const exportPdf = async () => {
    const canvas = getCanvas();
    if (!canvas) return;

    if (!token) {
      setStatusMessage('Login is required to export PDF.');
      return;
    }

    try {
      setIsExportingPdf(true);
      setStatusMessage('Generating PDF export...');

      const designJson = JSON.stringify(canvas.toJSON());
      const previewImage = canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });

      const exported = await exportPdfApi({
        token,
        designId: activeDesignId,
        designJson,
        previewImage,
        size: '6x4'
      });

      if (!exported?.pdfBase64) {
        throw new Error('PDF payload missing from server response.');
      }

      const blob = base64ToBlob(exported.pdfBase64, exported.mimeType || 'application/pdf');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exported.fileName || 'postcard-export.pdf';
      link.click();
      URL.revokeObjectURL(url);

      setStatusMessage('PDF exported and downloaded successfully.');
    } catch (error) {
      console.error('PDF export failed:', error);
      setStatusMessage(error.message || 'Failed to export PDF.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const updateOrderField = (field, value) => {
    setOrderForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const createPrintOrder = async () => {
    if (!token) {
      setStatusMessage('Login is required to create print orders.');
      return;
    }

    if (!activeDesignId) {
      setStatusMessage('Please save or load a design before creating an order.');
      return;
    }

    try {
      setIsCreatingOrder(true);

      const created = await createOrderApi({
        token,
        designId: activeDesignId,
        address: orderForm
      });

      setStatusMessage(`Print order #${created.order.id} created successfully.`);
      await loadMyOrders(token);
    } catch (error) {
      console.error('Create order failed:', error);
      setStatusMessage(error.message || 'Failed to create print order.');
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const getStatusDraft = (order) => {
    return statusDraftByOrderId[order.id] || order.status || 'pending';
  };

  const getProviderDraft = (order) => {
    return providerDraftByOrderId[order.id] || supportedProviders[0] || 'lob';
  };

  const updateStatusDraft = (orderId, value) => {
    setStatusDraftByOrderId((prev) => ({
      ...prev,
      [orderId]: value
    }));
  };

  const updateProviderDraft = (orderId, value) => {
    setProviderDraftByOrderId((prev) => ({
      ...prev,
      [orderId]: value
    }));
  };

  const updateOrderStatus = async (order) => {
    if (!token) {
      setStatusMessage('Login is required to update order status.');
      return;
    }

    const nextStatus = getStatusDraft(order);

    try {
      setUpdatingStatusOrderId(order.id);
      await updateOrderStatusApi({
        token,
        orderId: order.id,
        status: nextStatus
      });

      setStatusMessage(`Order #${order.id} status updated to ${nextStatus}.`);
      await loadMyOrders(token);
    } catch (error) {
      console.error('Update order status failed:', error);
      setStatusMessage(error.message || 'Failed to update order status.');
    } finally {
      setUpdatingStatusOrderId(null);
    }
  };

  const submitOrderToProvider = async (order) => {
    if (!token) {
      setStatusMessage('Login is required to submit orders to providers.');
      return;
    }

    const provider = getProviderDraft(order);

    try {
      setSubmittingProviderOrderId(order.id);
      await submitOrderToProviderApi({
        token,
        orderId: order.id,
        provider
      });

      setStatusMessage(`Order #${order.id} submitted to ${provider} (placeholder).`);
      await loadMyOrders(token);
    } catch (error) {
      console.error('Submit order to provider failed:', error);
      setStatusMessage(error.message || 'Failed to submit order to provider.');
    } finally {
      setSubmittingProviderOrderId(null);
    }
  };

  return (
    <section
      aria-label="Postcard editor"
      className="rounded-3xl bg-gradient-to-br from-blue-600 via-sky-500 to-amber-400 p-[1px] shadow-2xl"
    >
      <div className="rounded-3xl bg-white/95 p-4 md:p-6">
        <header className="mb-4 flex flex-col gap-2 md:mb-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
            Creative Postcard Editor
          </h2>
          <p className="max-w-3xl text-sm text-slate-700 md:text-base">
            Build eye-catching postcard designs for USA campaigns. Every layer is draggable, resizable, and rotatable.
          </p>
        </header>

        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <article className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-700">Design Actions</h3>

            <div className="flex flex-wrap gap-2">
              <button type="button" className={actionButton} onClick={addText} aria-label="Add text layer">
                Add Text
              </button>
              <button type="button" className={actionButton} onClick={() => addShape('rect')} aria-label="Add rectangle shape">
                Rectangle
              </button>
              <button type="button" className={actionButton} onClick={() => addShape('circle')} aria-label="Add circle shape">
                Circle
              </button>
              <button type="button" className={actionButton} onClick={() => addShape('triangle')} aria-label="Add triangle shape">
                Triangle
              </button>
              <button
                type="button"
                className={actionButton}
                onClick={() => fileInputRef.current?.click()}
                aria-label="Upload image to canvas"
              >
                Upload Image
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={onUploadImage}
                aria-label="Image file input"
              />
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
              <label htmlFor="qr-value" className="sr-only">
                QR code URL
              </label>
              <input
                id="qr-value"
                value={qrValue}
                onChange={(e) => setQrValue(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-brandBlue focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Paste URL for QR code"
              />
              <button type="button" className={primaryButton} onClick={addQrCode} aria-label="Add QR code to canvas">
                Add QR
              </button>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
              <label htmlFor="ai-prompt" className="sr-only">
                AI template prompt
              </label>
              <input
                id="ai-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                list="prompt-suggestions"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-brandBlue focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Example: Birthday postcard for John"
              />
              <datalist id="prompt-suggestions">
                {aiPrompts.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              <button
                type="button"
                className={primaryButton}
                onClick={applyAiTemplateFromApi}
                aria-label="Apply AI template suggestion"
                disabled={isGeneratingAi}
              >
                {isGeneratingAi ? 'Generating...' : 'AI Suggest'}
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className={primaryButton}
                onClick={saveDesign}
                aria-label="Save design JSON and PNG preview"
                disabled={isSavingDesign}
              >
                {isSavingDesign ? 'Saving...' : 'Save JSON + PNG'}
              </button>
              <button type="button" className={primaryButton} onClick={exportPng} aria-label="Export PNG image">
                Export PNG
              </button>
              <button
                type="button"
                className={`${buttonBase} bg-brandAmber text-slate-900 hover:-translate-y-0.5 hover:bg-amber-400 focus-visible:ring-brandAmber`}
                onClick={exportPdf}
                aria-label="Export PDF"
                disabled={isExportingPdf}
              >
                {isExportingPdf ? 'Exporting PDF...' : 'Export PDF'}
              </button>
              <button type="button" className={actionButton} onClick={removeSelected} aria-label="Delete selected object">
                Delete Selected
              </button>
              <button type="button" className={dangerButton} onClick={clearCanvas} aria-label="Clear all canvas objects">
                Clear Canvas
              </button>
            </div>

            <p className="mt-4 rounded-xl bg-slate-900 px-3 py-2 text-xs text-slate-100" role="status" aria-live="polite">
              {statusMessage}
            </p>
          </article>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-700">Canvas (600 x 400)</h3>
              <div className="overflow-auto rounded-xl border border-slate-200 bg-white p-2">
                <canvas ref={canvasElementRef} />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-700">Live Preview</h3>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {previewUrl ? (
                  <img src={previewUrl} alt="Live postcard preview" className="h-auto w-full transition duration-300 hover:scale-[1.01]" />
                ) : (
                  <p className="p-4 text-xs text-slate-500">Preview will appear after the first canvas update.</p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">My Designs</h3>
                {token && (
                  <button
                    type="button"
                    onClick={() => loadMyDesigns(token)}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    Refresh
                  </button>
                )}
              </div>

              {!token && (
                <p className="text-xs text-slate-500">Login to sync and view your saved cloud designs.</p>
              )}

              {token && isLoadingDesigns && <p className="text-xs text-slate-500">Loading designs...</p>}

              {token && !isLoadingDesigns && designs.length === 0 && (
                <p className="text-xs text-slate-500">No saved designs yet.</p>
              )}

              {token && designs.length > 0 && (
                <ul className="space-y-2">
                  {designs.slice(0, 8).map((item) => (
                    <li key={item.id} className="rounded-xl border border-slate-200 p-2">
                      <p className="text-xs font-semibold text-slate-700">Design #{item.id}</p>
                      <p className="text-[11px] text-slate-500">{formatDate(item.created_at)}</p>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                          {item.size || '6x4'}
                        </span>
                        <button
                          type="button"
                          onClick={() => loadDesignFromList(item)}
                          className="rounded-lg bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                        >
                          Load
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Create Print Order</h3>
                {activeDesignId ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                    Design #{activeDesignId}
                  </span>
                ) : (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    No design selected
                  </span>
                )}
              </div>

              <div className="grid gap-2">
                <input
                  type="text"
                  value={orderForm.full_name}
                  onChange={(e) => updateOrderField('full_name', e.target.value)}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                  placeholder="Recipient full name"
                />
                <input
                  type="text"
                  value={orderForm.line1}
                  onChange={(e) => updateOrderField('line1', e.target.value)}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                  placeholder="Address line 1"
                />
                <input
                  type="text"
                  value={orderForm.line2}
                  onChange={(e) => updateOrderField('line2', e.target.value)}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                  placeholder="Address line 2 (optional)"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={orderForm.city}
                    onChange={(e) => updateOrderField('city', e.target.value)}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                    placeholder="City"
                  />
                  <input
                    type="text"
                    value={orderForm.state}
                    onChange={(e) => updateOrderField('state', e.target.value)}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                    placeholder="State"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={orderForm.zip}
                    onChange={(e) => updateOrderField('zip', e.target.value)}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                    placeholder="ZIP"
                  />
                  <input
                    type="text"
                    value={orderForm.country}
                    onChange={(e) => updateOrderField('country', e.target.value)}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                    placeholder="Country"
                  />
                </div>

                <button
                  type="button"
                  onClick={createPrintOrder}
                  disabled={isCreatingOrder}
                  className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isCreatingOrder ? 'Creating Order...' : 'Create Print Order'}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Order Tracking</h3>
                {token && (
                  <button
                    type="button"
                    onClick={() => loadMyOrders(token)}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    Refresh
                  </button>
                )}
              </div>

              {!token && <p className="text-xs text-slate-500">Login to create and track print orders.</p>}
              {token && isLoadingOrders && <p className="text-xs text-slate-500">Loading orders...</p>}
              {token && !isLoadingOrders && orders.length === 0 && (
                <p className="text-xs text-slate-500">No print orders yet.</p>
              )}

              {token && orders.length > 0 && (
                <ul className="space-y-2">
                  {orders.slice(0, 8).map((item) => (
                    <li key={item.id} className="rounded-xl border border-slate-200 p-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-slate-700">Order #{item.id}</p>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusChipClass(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500">Design #{item.design_id}</p>
                      <p className="text-[11px] text-slate-500">{formatDate(item.created_at)}</p>
                      <p className="text-[11px] text-slate-500">
                        Provider Ref: {item.print_api_id || 'Pending assignment'}
                      </p>

                      <div className="mt-2 flex flex-wrap gap-1">
                        {ORDER_TIMELINE.map((step) => (
                          <span
                            key={`${item.id}-${step}`}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${timelineStepClass(item.status, step)}`}
                          >
                            {step}
                          </span>
                        ))}
                        {String(item.status).toLowerCase() === 'failed' && (
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                            failed
                          </span>
                        )}
                      </div>

                      <div className="mt-2 grid gap-2">
                        <div className="flex gap-2">
                          <select
                            value={getStatusDraft(item)}
                            onChange={(e) => updateStatusDraft(item.id, e.target.value)}
                            className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-xs"
                          >
                            {allowedOrderStatuses.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => updateOrderStatus(item)}
                            disabled={updatingStatusOrderId === item.id}
                            className="rounded-lg bg-slate-700 px-2 py-1 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {updatingStatusOrderId === item.id ? 'Updating...' : 'Set Status'}
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <select
                            value={getProviderDraft(item)}
                            onChange={(e) => updateProviderDraft(item.id, e.target.value)}
                            className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-xs"
                          >
                            {supportedProviders.map((provider) => (
                              <option key={provider} value={provider}>
                                {provider}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => submitOrderToProvider(item)}
                            disabled={submittingProviderOrderId === item.id}
                            className="rounded-lg bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {submittingProviderOrderId === item.id ? 'Submitting...' : 'Submit Print'}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default Editor;






